import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, Alert, Modal, FlatList, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { WorkStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { JOB_CATEGORIES } from '../../data/jobCategories';
import ThemedText from '../../components/common/ThemedText';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';

type AddJobNavigationProp = StackNavigationProp<WorkStackParamList, 'AddJob'>;

const AddJobScreen: React.FC = () => {
  const navigation = useNavigation<AddJobNavigationProp>();
  const { theme } = useTheme();
  const { createJob } = useWork();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [errors, setErrors] = useState({ title: false, category: false, description: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { keyboardHeight } = useKeyboardHeight();

  useEffect(() => {
    console.log('🔍 [AddJobScreen] bottom spacing => headerHeight:', headerHeight, 'bottomInset:', bottomInset, 'keyboardHeight:', keyboardHeight);
  }, [headerHeight, bottomInset, keyboardHeight]);

  const handlePostJob = async () => {
    // Reset errors
    setErrors({ title: false, category: false, description: false });

    // Validation
    const newErrors = {
      title: !title.trim(),
      category: !category.trim(),
      description: !description.trim(),
    };

    setErrors(newErrors);

    if (newErrors.title || newErrors.category || newErrors.description) {
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('🔍 Creating job...');
      await createJob({
        type: 'contract',
        title: title.trim(),
        category,
        description: description.trim(),
        budget: budget ? parseFloat(budget.replace(/[^0-9.]/g, '')) : undefined,
      });

      console.log('🔍 Job created successfully');
      Alert.alert(
        'Success',
        'Job posted successfully!',
        [
          {
            text: 'OK',
            onPress: () => {
              console.log('🔍 Success alert OK pressed');
              console.log('🔍 Can go back:', navigation.canGoBack());
              if (navigation.canGoBack()) {
                console.log('🔍 Going back after job creation...');
                navigation.goBack();
              } else {
                console.log('🔍 Cannot go back, navigating to WorkHome');
                navigation.navigate('WorkHome');
              }
            },
          },
        ]
      );
    } catch (error) {
      console.error('🔍 Error creating job:', error);
      Alert.alert('Error', 'Failed to post job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    console.log('🔍 handleCancel called');
    console.log('🔍 Can go back:', navigation.canGoBack());
    console.log('🔍 Navigation state:', navigation.getState());
    
    if (navigation.canGoBack()) {
      console.log('🔍 Going back...');
      navigation.goBack();
    } else {
      console.log('🔍 Cannot go back, navigating to WorkHome');
      navigation.navigate('WorkHome');
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['left', 'right']}>
      <View
        style={[styles.header, { borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Post a Job</ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Job Title *</ThemedText>
            <TextInput
              style={[
                styles.input, 
                { backgroundColor: theme.backgroundSecondary, color: theme.text },
                errors.title && { borderWidth: 2, borderColor: theme.error }
              ]}
              placeholder="e.g., Construction of House"
              value={title}
              onChangeText={(text) => {
                setTitle(text);
                if (errors.title) setErrors({ ...errors, title: false });
              }}
              placeholderTextColor={theme.textSecondary}
            />
            {errors.title && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Category *</ThemedText>
            <TouchableOpacity
              style={[
                styles.input, 
                styles.pickerInput, 
                { backgroundColor: theme.backgroundSecondary, borderColor: theme.border },
                errors.category && { borderWidth: 2, borderColor: theme.error }
              ]}
              onPress={() => setShowCategoryPicker(true)}
            >
              <ThemedText style={[styles.pickerText, { color: category ? theme.text : theme.textSecondary }]}>
                {category || 'Select a category'}
              </ThemedText>
              <Ionicons name="chevron-down" size={20} color={theme.textSecondary} />
            </TouchableOpacity>
            {errors.category && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Description *</ThemedText>
            <TextInput
              style={[
                styles.input, 
                styles.textArea, 
                { backgroundColor: theme.backgroundSecondary, color: theme.text },
                errors.description && { borderWidth: 2, borderColor: theme.error }
              ]}
              placeholder="Describe the job requirements..."
              value={description}
              onChangeText={(text) => {
                setDescription(text);
                if (errors.description) setErrors({ ...errors, description: false });
              }}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              placeholderTextColor={theme.textSecondary}
            />
            {errors.description && (
              <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
            )}
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Budget (Optional)</ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text }]}
              placeholder="e.g., 5000"
              value={budget}
              onChangeText={setBudget}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={[styles.cancelButton, { borderColor: theme.border }]} onPress={handleCancel} disabled={isSubmitting}>
              <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>Cancel</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.postButton, { backgroundColor: theme.accent, opacity: isSubmitting ? 0.6 : 1 }]} 
              onPress={handlePostJob}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator color={theme.textInverse} />
              ) : (
                <ThemedText style={[styles.postButtonText, { color: theme.textInverse }]}>Post Job</ThemedText>
              )}
            </TouchableOpacity>
          </View>

          <FormFooterSpacer
            headerHeight={headerHeight}
            bottomInset={bottomInset}
            keyboardHeight={keyboardHeight}
          />
        </View>
      </ScrollView>

      {/* Category Picker Modal */}
      <Modal
        visible={showCategoryPicker}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowCategoryPicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.backgroundSecondary }]}>
            <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.modalTitle, { color: theme.text }]}>Select Category</ThemedText>
              <TouchableOpacity onPress={() => setShowCategoryPicker(false)}>
                <Ionicons name="close" size={24} color={theme.textSecondary} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={JOB_CATEGORIES}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.categoryOption,
                    { borderBottomColor: theme.border },
                    category === item && { backgroundColor: theme.backgroundSecondary },
                  ]}
                  onPress={() => {
                    setCategory(item);
                    setShowCategoryPicker(false);
                    if (errors.category) setErrors({ ...errors, category: false });
                  }}
                >
                  <ThemedText style={[styles.categoryOptionText, { color: theme.text }]}>{item}</ThemedText>
                  {category === item && <Ionicons name="checkmark" size={24} color={theme.accent} />}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  form: {
    padding: 16,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
  },
  pickerInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
  },
  pickerText: {
    fontSize: 16,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  postButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  postButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  categoryOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  categoryOptionText: {
    fontSize: 16,
    fontWeight: '500',
  },
});

export default AddJobScreen;
